import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, concatMap, catchError, withLatestFrom } from 'rxjs/operators';
import { GridDataService } from '../services/grid-data.service';
import {
  GridDataRefreshFailedAction,
  GridDataRefreshSuccessAction,
  GridSetStateAndRefreshData,
  RefreshGridDataAction,
} from '../actions/grid.actions';
import { GridsState } from '../reducers/grid.reducers';
import { translateDataSourceResultGroups } from '@progress/kendo-data-query';
import { Observable, of, Subscriber } from 'rxjs';
import { getGridById } from '../selectors/grid.selectors';
import { createDefaultGridPayload } from '../models/gridPayload';
import { Grid } from '../models/grid.model';
import { GridDataResponse } from '../services/grid-data.service';

export function withBetterLatestFrom<TSource, TSecond>(second: (input: TSource) => Observable<TSecond>) {
  return function latestFrom(source: Observable<TSource>): Observable<[TSource, TSecond]> {
    return new Observable<[TSource, TSecond]>((subscriber: Subscriber<[TSource, TSecond]>) => {
      const subscription = source
        .pipe(concatMap((action: TSource) => of(action).pipe(withLatestFrom(second(action)))))
        .subscribe({
          next: (value: [TSource, TSecond]) => {
            try {
              subscriber.next(value);
            } catch (err) {
              subscriber.error(err);
            }
          },
          error: (err: unknown) => subscriber.error(err),
          complete: () => subscriber.complete(),
        });
      return subscription;
    });
  };
}

@Injectable()
export class GridsEffects {
  private gridsService = inject(GridDataService);
  private actions$ = inject(Actions);
  private store = inject<Store<GridsState>>(Store);

  loadGridData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RefreshGridDataAction),
      withBetterLatestFrom((x) => this.store.select(getGridById(x.id))),
      concatMap(([action, grid]: [ReturnType<typeof RefreshGridDataAction>, Grid | undefined]) => {
        const settings = grid?.state ?? action.settings ?? {};
        const addition = action.addition ?? null;
        const payload = createDefaultGridPayload(action.payload ?? grid?.payload);
        return this.gridsService.fetchData(action.urlPostfix, settings, addition, payload).pipe(
          map((x: GridDataResponse) => {
            const hasGroups = Boolean(settings.group?.length);
            return GridDataRefreshSuccessAction({
              id: action.id,
              gridResponse: (hasGroups ? translateDataSourceResultGroups(x.data) : x.data) as unknown[],
              total: x.total,
              gridErrors: x.errors,
            });
          }),
          catchError(() => {
            return of(GridDataRefreshFailedAction({ id: action.id }));
          })
        );
      })
    )
  );

  setStateAndLoadGridData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GridSetStateAndRefreshData),
      withBetterLatestFrom((x) => this.store.select(getGridById(x.id))),
      concatMap(([action, grid]: [ReturnType<typeof GridSetStateAndRefreshData>, Grid | undefined]) => {
        const settings = grid?.state ?? action.newState;
        const payload = createDefaultGridPayload(grid?.payload);
        return this.gridsService.fetchData(action.urlPostfix, settings, null, payload).pipe(
          map((x: GridDataResponse) => {
            const hasGroups = Boolean(settings.group?.length);
            return GridDataRefreshSuccessAction({
              id: action.id,
              gridResponse: (hasGroups ? translateDataSourceResultGroups(x.data) : x.data) as unknown[],
              total: x.total,
              gridErrors: x.errors,
            });
          }),
          catchError(() => {
            return of(GridDataRefreshFailedAction({ id: action.id }));
          })
        );
      })
    )
  );
}
